/* eslint-disable css-modules/no-unused-class  */
import { FC, useEffect, useState } from 'react';
import { CardLoader, ContentCard, Icon, UiLibrary } from 'learning-panel-components';
import { translate } from 'translations';
import { getPlatformUrl } from 'utils';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsLeanUser, selectIsPLG } from '@redux/slices/userSlice';
import CourseTop from './courseTop/courseTop';
import styles from './course.module.scss';
import QuizCard from './quizCard';

const Course: FC<CourseProps> = props => {
    const { course, courseLessons, quiz, isLoading, unavailableCourseLessons, onContactUsClick } = props;

    const [courseToUnits, setCourseToUnits] = useState<{ [unitNme: string]: CourseLesson[] }>({});
    const [isOrderingUnits, setIsOrderingUnits] = useState<boolean>(false);

    const isLeanUser = useSelector(selectIsLeanUser);
    const isPLG = useSelector(selectIsPLG);
    const navigate = useNavigate();

    const setBackNavigation = () => {
        if (!document.referrer || document.referrer.includes('home')) navigate('/app/home');
        else navigate('/app/learn');
    };

    useEffect(() => {
        if (course?.units && courseLessons) {
            setIsOrderingUnits(true);
            const unitsWithOrder = course.units.map(unit => {
                const unitLessons = courseLessons
                    .filter(lesson => unit.unitLessons.includes(lesson.uuid))
                    .sort(
                        (a, b) =>
                            courseLessons.findIndex(l => l.uuid === a.uuid) -
                            courseLessons.findIndex(l => l.uuid === b.uuid)
                    );
                const minIndex = Math.min(
                    ...unitLessons.map(lesson => courseLessons.findIndex(l => l.uuid === lesson.uuid))
                );
                return {
                    unitName: unit.unitName,
                    unitLessons,
                    minIndex
                };
            });

            unitsWithOrder.sort((a, b) => a.minIndex - b.minIndex);
            const sortedUnits: { [unitName: string]: CourseLesson[] } = {};
            unitsWithOrder.forEach(unit => {
                sortedUnits[unit.unitName] = unit.unitLessons;
            });

            setCourseToUnits(sortedUnits);
            setIsOrderingUnits(false);
        }
    }, [course, courseLessons]);

    const getLessonsList = (lessons: CourseLesson[], isLeanDisabled = false) => {
        return (
            <div className={styles.list}>
                <UiLibrary.Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={3}>
                    {isLoading || isOrderingUnits
                        ? new Array(20).fill('').map((_, i) => (
                              <UiLibrary.Grid item key={i}>
                                  <CardLoader width="16rem" height="15.34rem" />
                              </UiLibrary.Grid>
                          ))
                        : lessons?.map((lesson: any) => (
                              <UiLibrary.Grid item key={lesson.uuid}>
                                  <ContentCard
                                      uuid={lesson.uuid}
                                      type={'lesson'}
                                      imageUrl={
                                          lesson.iconUrl
                                              ? lesson.iconUrl
                                              : `${process.env.PUBLIC_URL}/images/lessonDefaultCard.webp`
                                      }
                                      name={lesson.name}
                                      displayName={lesson.displayName}
                                      description={lesson.description}
                                      timeOrDate={lesson.duration}
                                      cardUrl={lesson.lessonUrl}
                                      links={lesson.links}
                                      tags={lesson.tags}
                                      useOverlay={Boolean(lesson.iconUrl)}
                                      status={lesson.isCompleted ? 'completed' : 'notStarted'}
                                      isLeanDisabled={isLeanDisabled}
                                      score={lesson.score}
                                      doneRecently={lesson.doneRecently}
                                      earnedPoints={lesson.earnedPoints}
                                      onContactUsClick={isPLG ? onContactUsClick : undefined}
                                      tagsNames={lesson.tagsNames}
                                      systemCourseDisplayName={lesson.systemCourseDisplayName}
                                      customCourseDisplayNames={lesson.customCourseDisplayNames}
                                  />
                              </UiLibrary.Grid>
                          ))}
                </UiLibrary.Grid>
            </div>
        );
    };

    return (
        <div id="course-page" className={styles['course-wrapper-full-width']}>
            <div className={styles.back}>
                <div className={styles['back-content']} onClick={(): void => setBackNavigation()}>
                    <Icon name="back" className={styles['back-icon']} />
                    <div>{translate('Back')}</div>
                </div>
            </div>
            <div className={styles['course-content']}>
                {isLoading ? (
                    <div></div>
                ) : (
                    <div className={styles['course-banner']}>
                        <CourseTop
                            name={course.name}
                            displayName={course.displayName}
                            description={course.description}
                            playNextLessonUrl={
                                course.nextLessonUrl ? `${getPlatformUrl()}${course.nextLessonUrl}` : undefined
                            }
                            courseImage={course.courseImageSrc}
                            completionRate={course.userCompletionRate}
                            coins={course.coins}
                        />
                    </div>
                )}

                {isLoading ? (
                    <div></div>
                ) : (
                    !course?.units && (
                        <div className={styles.title}>
                            {translate('LessonsInThisCourse', {
                                numberOfLessons: String(courseLessons.length)
                            })}
                        </div>
                    )
                )}

                {!isOrderingUnits && !course?.units && getLessonsList(courseLessons)}
                {!isOrderingUnits && course?.units && (
                    <div>
                        {Object.keys(courseToUnits).map((unitName: string) => (
                            <div key={unitName}>
                                <div className={styles.title}>{unitName}</div>
                                {getLessonsList(courseToUnits[unitName])}
                            </div>
                        ))}
                    </div>
                )}
                {quiz && <div className={styles.title}>{translate('CertificateTest')}</div>}
                {quiz && (
                    <div className={styles.list}>
                        <QuizCard {...quiz} courseCompleted={course.userCompletionRate === 100} />
                    </div>
                )}

                {isLeanUser && unavailableCourseLessons?.length > 0 && (
                    <>
                        {isLoading ? (
                            <div></div>
                        ) : (
                            <div className={styles.title}>
                                {translate('AvailableForPremiumUsersOnly')} ({unavailableCourseLessons.length})
                            </div>
                        )}

                        {getLessonsList(unavailableCourseLessons, true)}
                    </>
                )}
            </div>
        </div>
    );
};

export default Course;

interface CourseLesson {
    uuid: string;
    isCompleted: boolean;
    imageUrl: string;
    displayName: string;
    name: string;
    lessonUrl: string;
    score?: number;
    duration: string;
}

interface CourseProps {
    onBack: Function;
    isLoading: boolean;
    course: {
        uuid: string;
        name: string;
        displayName: string;
        description: string;
        courseRate: number;
        alreadyTookUsers: { nickname: string; profilePicture?: string }[];
        totalTookUsers: number;
        nextLessonUrl: string;
        courseImageSrc: string;
        userCompletionRate: number;
        coins: number;
        units?: { unitName: string; unitLessons: string[] }[];
    };
    quiz: {
        quizId: number;
        status: 'completed' | 'attempts_exceeded' | 'not_completed';
        numberOfQuestions: number;
        attemptsAllowed: number;
        incorrectQuestionsAllowed: number;
    };
    userAvatar?: string;
    userNickname: string;
    courseLessons: CourseLesson[];
    unavailableCourseLessons: CourseLesson[];
    onContactUsClick?: Function;
}
